import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiService, Match } from '../services/api';

const LOGO_BASE =
  'https://pub-e0ccb659989a49a297dc22911f822b08.r2.dev/photos/teams/logos';

const getTeamLogoUrl = (teamId: string): string => `${LOGO_BASE}/${teamId}.png`;

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (timeString?: string): string => {
  if (!timeString) return '';
  return timeString.substring(0, 5);
};

const matchSortKey = (match: Match): [string, string, number] => [
  match.date || '',
  match.time || '',
  match.match_no || 0,
];

const YearSchedule: React.FC = () => {
  const { year } = useParams<{ year: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroup = searchParams.get('group') || '';

  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!year) {
        setError('Invalid URL parameters');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const matchesData = await apiService.getMatches();
        if (!Array.isArray(matchesData)) {
          throw new Error('Invalid data format received from API');
        }

        const yearNum = parseInt(year, 10);
        const yearMatches = matchesData
          .filter((match) => match.year === yearNum)
          .sort((a, b) => {
            const [dateA, timeA, noA] = matchSortKey(a);
            const [dateB, timeB, noB] = matchSortKey(b);
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            if (timeA !== timeB) return timeA.localeCompare(timeB);
            return noA - noB;
          });

        setMatches(yearMatches);
      } catch (err) {
        console.error('YearSchedule: Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [year]);

  const groups = useMemo(
    () => Array.from(new Set(matches.map((m) => m.group).filter(Boolean))).sort(),
    [matches]
  );

  const filteredMatches = useMemo(() => {
    if (!selectedGroup) return matches;
    return matches.filter((match) => match.group === selectedGroup);
  }, [matches, selectedGroup]);

  const handleGroupFilter = (group: string) => {
    if (!group) {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ group }, { replace: true });
  };

  const handleLogoError = (teamId: string) => {
    setFailedLogos((prev) => new Set(Array.from(prev).concat(teamId)));
  };

  const logoFailed = (teamId: string): boolean => failedLogos.has(teamId);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#e3f2fd', minHeight: '100vh' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: 'red', backgroundColor: '#e3f2fd', minHeight: '100vh' }}>
        {error}
      </div>
    );
  }

  if (!year) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#e3f2fd', minHeight: '100vh' }}>
        Invalid URL parameters
      </div>
    );
  }

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '20px',
    border: active ? '2px solid #3182ce' : '2px solid #cbd5e0',
    backgroundColor: active ? '#3182ce' : '#fff',
    color: active ? '#fff' : '#2d3748',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  return (
    <div
      style={{
        maxWidth: '100%',
        margin: '0 auto',
        padding: isDesktop ? '20px 40px' : '4px',
        backgroundColor: '#e3f2fd',
        minHeight: '100vh',
      }}
    >
      <div style={{ marginBottom: '20px' }}>
        <Link
          to={`/summer-league/${year}`}
          style={{
            color: '#0066cc',
            textDecoration: 'none',
            fontSize: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          ← 返回
        </Link>
      </div>

      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#2d3748', marginBottom: '8px' }}>賽程</h1>
        <h2 style={{ fontSize: '1.8rem', color: '#4a5568', marginBottom: '8px' }}>
          Summer League {year}
        </h2>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '10px',
          marginBottom: '28px',
        }}
      >
        <button type="button" style={chipStyle(!selectedGroup)} onClick={() => handleGroupFilter('')}>
          全部
        </button>
        {groups.map((group) => (
          <button
            key={group}
            type="button"
            style={chipStyle(selectedGroup === group)}
            onClick={() => handleGroupFilter(group)}
          >
            {group}
          </button>
        ))}
      </div>

      <div>
        <h3
          style={{
            fontSize: '1.5rem',
            color: '#2d3748',
            marginBottom: '24px',
            borderBottom: '2px solid #e2e8f0',
            paddingBottom: '8px',
          }}
        >
          比賽結果
          {selectedGroup ? ` · ${selectedGroup}` : ''}
        </h3>

        {filteredMatches.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {filteredMatches.map((match) => (
              <div
                key={match.id}
                className="schedule-match-row"
                style={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  gap: '6px',
                  flexWrap: 'wrap',
                  minHeight: '80px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: isDesktop ? '120px' : '60px',
                    flexShrink: 0,
                    position: isDesktop ? 'absolute' : 'static',
                    left: isDesktop ? '20px' : 'auto',
                  }}
                >
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#2d3748', marginBottom: '4px' }}>
                    場次 {match.match_no}
                  </div>
                  {!selectedGroup && (
                    <div style={{ fontSize: '0.8rem', color: '#3182ce', fontWeight: 600, marginBottom: '2px' }}>
                      {match.group}
                    </div>
                  )}
                  <div style={{ fontSize: '0.9rem', color: '#718096' }}>
                    {formatDate(match.date)} {match.time && formatTime(match.time)}
                  </div>
                </div>

                <div
                  className="schedule-team-col"
                  style={{
                    minWidth: isDesktop ? '120px' : '60px',
                    flex: isDesktop ? '0 0 auto' : '1',
                    flexShrink: 0,
                  }}
                >
                  {match.team1 ? (
                    <Link
                      to={`/summer-league/${year}/${match.group}/teams/${match.team1.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <div className="schedule-team-logo-slot">
                        {!logoFailed(match.team1.id) ? (
                          <img
                            src={getTeamLogoUrl(match.team1.id)}
                            alt={`${match.team1.name} Logo`}
                            onError={() => handleLogoError(match.team1.id)}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '8px',
                              border: '2px solid #ddd',
                            }}
                          />
                        )}
                      </div>
                      <div
                        className="schedule-team-name"
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#2d3748',
                          textAlign: 'center',
                          marginBottom: '8px',
                        }}
                      >
                        {match.team1.name}
                      </div>
                    </Link>
                  ) : (
                    <>
                      <div
                        className="schedule-team-logo-slot"
                        style={{
                          backgroundColor: '#f0f0f0',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          fontSize: '20px',
                          color: '#666',
                        }}
                      >
                        TBD
                      </div>
                      <div
                        className="schedule-team-name"
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#2d3748',
                          textAlign: 'center',
                          marginBottom: '8px',
                        }}
                      >
                        TBD
                      </div>
                    </>
                  )}
                  {match.score1 !== null && match.score1 !== undefined && (
                    <div className="schedule-team-score" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3182ce' }}>
                      {match.score1}
                    </div>
                  )}
                </div>

                <div
                  className="schedule-vs"
                  style={{
                    fontSize: isDesktop ? '1.2rem' : '1rem',
                    fontWeight: 'bold',
                    color: '#2d3748',
                    margin: isDesktop ? '0 20px' : '0 8px',
                    flexShrink: 0,
                  }}
                >
                  vs
                </div>

                <div
                  className="schedule-team-col"
                  style={{
                    minWidth: isDesktop ? '120px' : '60px',
                    flex: isDesktop ? '0 0 auto' : '1',
                    flexShrink: 0,
                  }}
                >
                  {match.team2 ? (
                    <Link
                      to={`/summer-league/${year}/${match.group}/teams/${match.team2.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <div className="schedule-team-logo-slot">
                        {!logoFailed(match.team2.id) ? (
                          <img
                            src={getTeamLogoUrl(match.team2.id)}
                            alt={`${match.team2.name} Logo`}
                            onError={() => handleLogoError(match.team2.id)}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '8px',
                              border: '2px solid #ddd',
                            }}
                          />
                        )}
                      </div>
                      <div
                        className="schedule-team-name"
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#2d3748',
                          textAlign: 'center',
                          marginBottom: '8px',
                        }}
                      >
                        {match.team2.name}
                      </div>
                    </Link>
                  ) : (
                    <>
                      <div
                        className="schedule-team-logo-slot"
                        style={{
                          backgroundColor: '#f0f0f0',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          fontSize: '20px',
                          color: '#666',
                        }}
                      >
                        TBD
                      </div>
                      <div
                        className="schedule-team-name"
                        style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#2d3748',
                          textAlign: 'center',
                          marginBottom: '8px',
                        }}
                      >
                        TBD
                      </div>
                    </>
                  )}
                  {match.score2 !== null && match.score2 !== undefined && (
                    <div className="schedule-team-score" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3182ce' }}>
                      {match.score2}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    minWidth: isDesktop ? '120px' : '60px',
                    flexShrink: 0,
                    position: isDesktop ? 'absolute' : 'static',
                    right: isDesktop ? '20px' : 'auto',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: '#718096',
                      marginBottom: '8px',
                      minHeight: '20px',
                      textAlign: 'right',
                      wordBreak: 'break-word',
                      maxWidth: '100px',
                      lineHeight: '1.2',
                    }}
                  >
                    {match.venue || ''}
                  </div>
                  <Link
                    to={`/summer-league/matches/${match.id}`}
                    style={{
                      color: '#3182ce',
                      textDecoration: 'underline',
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    比賽結果
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#718096', padding: '40px' }}>
            No matches found{selectedGroup ? ` for ${selectedGroup}` : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default YearSchedule;
